import type {
  Applicants,
  JobSeekerProfileSubscription,
} from "../../../__generated__/types.gqlgenerated";
import type { ViewjobQuery } from "../__generated__/viewjob.gqlgenerated";
import type {
  ApplicantInsightsCta,
  ApplicantInsightsModel,
} from "../components/mosaic/ApplicantInsights/types";
import type { useMosaicProviderProBannerVjTogPayload } from "../hooks/useMosaicProviderProBannerVjTog";
import type { JobSeekerProfileFragment } from "./__generated__/jobSeekerProfile.gqlgenerated";

export interface ApplicantInsightsProctorData {
  hasCtaAccess: boolean;
  isJsPremiumApplicantInsightsActive: boolean;
  isJsProVjProInsightsTogActive: boolean;
  proctorPayload: ReturnType<typeof useMosaicProviderProBannerVjTogPayload>;
  variant: number;
}

export const getApplicantInsightsModel = (
  jobSeekerProfile: JobSeekerProfileFragment,
  viewjob: NonNullable<ViewjobQuery["viewjob"]>,
  proctorData: ApplicantInsightsProctorData | undefined,
): ApplicantInsightsModel | null => {
  const applicants: Applicants | undefined | null = viewjob.job?.applicants;
  const applicantInsightsAccess =
    jobSeekerProfile.profile?.jsProPremiumAccess?.applicantInsights?.access;
  const hasInsights = applicants?.hasInsights;
  const subscription = jobSeekerProfile.profile?.subscription;

  if (
    applicants === null ||
    applicants === undefined ||
    proctorData === undefined ||
    !hasInsights
  ) {
    return null;
  }
  if (
    hasApplicantInsightsAccess(applicantInsightsAccess, proctorData) ||
    hasConsolidatedProInsightsAccess(
      applicantInsightsAccess,
      proctorData.isJsProVjProInsightsTogActive,
    )
  ) {
    return buildApplicantInsightsModel(applicants);
  }

  if (proctorData.hasCtaAccess) {
    return buildApplicantInsightsModelForCta(
      applicants,
      subscription,
      proctorData.proctorPayload,
      proctorData.variant,
    );
  }

  return null;
};

const hasApplicantInsightsAccess = (
  access: "DISABLED" | "ENABLED" | null | undefined,
  proctorData: ApplicantInsightsProctorData,
): boolean => {
  return access === "ENABLED" && proctorData.isJsPremiumApplicantInsightsActive;
};

const mapInsightItems = <T extends { title?: string | null; pct?: number | null }>(
  items: (T | null)[],
): { title: string; percent: number }[] => {
  return items
    .filter((item): item is T => item?.title != null && item?.pct != null)
    .map((item) => ({ title: item.title!, percent: item.pct! }));
};

const buildApplicantInsightsModel = (applicants: Applicants): ApplicantInsightsModel => {
  return {
    count: {
      totalCount: applicants.count.total,
      last24hCount: applicants.count.last24h,
    },
    workExperience: mapInsightItems(applicants.workExperience),
    education: mapInsightItems(applicants.education),
    skills: mapInsightItems(applicants.skills),
    certificates: mapInsightItems(applicants.certificates),
  };
};

const hasConsolidatedProInsightsAccess = (
  access: "DISABLED" | "ENABLED" | null | undefined,
  isJsProVjProInsightsTogActive: boolean,
): boolean => {
  return isJsProVjProInsightsTogActive && access !== null && access !== undefined;
};

const buildApplicantInsightsModelForCta = (
  applicants: Applicants,
  subscription: JobSeekerProfileSubscription | null | undefined,
  proctorPayload: ReturnType<typeof useMosaicProviderProBannerVjTogPayload>,
  variant: number,
): ApplicantInsightsModel => {
  return {
    count: {
      totalCount: -1,
      last24hCount: applicants.count.last24h,
    },
    workExperience: [],
    education: [],
    skills: [],
    certificates: [],
    previewInsight: applicants.previewInsight
      ? { title: applicants.previewInsight.title, percent: applicants.previewInsight.pct }
      : undefined,
    cta: buildApplicantInsightsCtaModel(subscription, proctorPayload, variant),
  };
};

const buildApplicantInsightsCtaModel = (
  subscription: JobSeekerProfileSubscription | null | undefined,
  proctorPayload: ReturnType<typeof useMosaicProviderProBannerVjTogPayload>,
  variant: number,
): ApplicantInsightsCta => {
  let buttonText = "";

  if (proctorPayload !== null && typeof proctorPayload.ctaButtonText !== "string") {
    const ctaButtonText = proctorPayload.ctaButtonText;
    buttonText = isEligibleForFreeTrial(subscription)
      ? ctaButtonText.freeTrial
      : ctaButtonText.default;
  }
  if (proctorPayload !== null && typeof proctorPayload.ctaButtonText === "string") {
    buttonText = proctorPayload.ctaButtonText;
  }

  const contentText = proctorPayload?.ctaContentText ?? "";

  return {
    buttonText,
    contentText,
    variant,
  };
};

const isEligibleForFreeTrial = (
  subscription: JobSeekerProfileSubscription | null | undefined,
): boolean => {
  return subscription === null || subscription === undefined || subscription.status === "NONE";
};
